<template>
  <Teleport to="body">
    <Transition name="urgent">
      <div v-if="open" class="urgent-overlay">
        <div
          class="urgent-card"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="urgent-alert-title"
          @click.stop
        >
          <div class="urgent-accent" />

          <header class="urgent-header">
            <span class="urgent-icon-wrapper">
              <AlertTriangle class="urgent-icon" />
              <span class="urgent-icon-pulse" aria-hidden="true" />
            </span>
            <div class="urgent-header-text">
              <span id="urgent-alert-title" class="urgent-title">{{ $gt('Dringende Nachrichten') }}</span>
              <span class="urgent-subtitle">
                {{ urgentItems.length === 1
                  ? $gt('Eine ungelesene Nachricht wurde als dringend markiert.')
                  : $gt('Ungelesene Nachrichten wurden als dringend markiert.') }}
              </span>
            </div>
            <span class="urgent-count">{{ urgentItems.length }}</span>
          </header>

          <ul class="urgent-list">
            <li v-for="item in urgentItems" :key="itemKey(item)" class="urgent-item">
              <div class="urgent-item-content">
                <span class="urgent-item-title">{{ item.title }}</span>
                <span class="urgent-item-subtitle">{{ item.subtitle }}</span>
                <span class="urgent-item-meta">
                  <span v-if="item.caseNumber">{{ item.caseNumber }}</span>
                  <span v-if="item.caseNumber && item.timeAgo" class="urgent-item-dot" aria-hidden="true">·</span>
                  <span v-if="item.timeAgo">{{ formatTime(item.timeAgo) }}</span>
                </span>
              </div>
              <button type="button" class="urgent-open-btn" @click="openItem(item)">
                {{ $gt('Öffnen') }}
              </button>
            </li>
          </ul>

          <footer class="urgent-footer">
            <button type="button" class="urgent-later-btn" @click="dismissAll">
              {{ $gt('Später') }}
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { reactive } from "vue";
import { AlertTriangle } from "lucide-vue-next";
import { getNotificationSummary } from "@/services/notificationApi";
import { useAuthStore } from "@/stores/authStore.ts";

// ── Shared state ("Bus") zwischen NotificationBell und diesem Overlay ────────
// Die Glocke pollt ohnehin alle 60s die Notifications und meldet nach jedem
// erfolgreichen Fetch die dringenden Items hierher (reportUrgentItems).
// So gibt es keine doppelten Requests, solange die Glocke gemountet ist.
export const urgentAlertBus = reactive({
  items: [],
  lastReportAt: 0,
});

export function reportUrgentItems(items) {
  // Defensiv: nur Items mit explizitem isUrgent === true zählen. Fehlt das
  // Feld (Backend noch nicht ausgerollt), bleibt die Liste leer und die App
  // verhält sich exakt wie bisher.
  urgentAlertBus.items = Array.isArray(items)
    ? items.filter((item) => item && item.isUrgent === true)
    : [];
  urgentAlertBus.lastReportAt = Date.now();
}

const STORAGE_KEY = "kars.urgentDismissed";
// Fallback-Polling nur, wenn die Glocke (Navigation) nicht gemountet ist und
// deshalb länger als STALE_REPORT_MS kein Report ankam.
const FALLBACK_POLL_MS = 60000;
const STALE_REPORT_MS = 90000;
const INITIAL_CHECK_DELAY_MS = 8000;

function readDismissedMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeDismissedMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage nicht verfügbar → Popup erscheint schlimmstenfalls erneut
  }
}

export default {
  name: "UrgentMessageAlert",

  components: { AlertTriangle },

  data() {
    return {
      open: false,
      fallbackTimer: null,
      initialTimer: null,
    };
  },

  computed: {
    urgentItems() {
      return urgentAlertBus.items;
    },
  },

  watch: {
    // Jeder Report (Glocke oder Fallback) ersetzt das Array → Watcher feuert.
    urgentItems(items) {
      this.evaluate(items);
    },
  },

  mounted() {
    // Falls die Glocke bereits gemeldet hat (z. B. nach Routenwechsel), sofort
    // auswerten.
    this.evaluate(this.urgentItems);

    // Fallback: Auf Seiten ohne Navigation/Glocke selbst pollen, damit das
    // Popup wirklich auf jeder Seite erscheint. Solange die Glocke regelmäßig
    // meldet, wird hier nichts gefetcht.
    this.initialTimer = setTimeout(() => this.fetchIfStale(), INITIAL_CHECK_DELAY_MS);
    this.fallbackTimer = setInterval(() => this.fetchIfStale(), FALLBACK_POLL_MS);
  },

  beforeUnmount() {
    if (this.fallbackTimer) clearInterval(this.fallbackTimer);
    if (this.initialTimer) clearTimeout(this.initialTimer);
  },

  methods: {
    async fetchIfStale() {
      if (!useAuthStore().isAuthenticated) return;
      if (Date.now() - urgentAlertBus.lastReportAt < STALE_REPORT_MS) return;
      try {
        const summary = await getNotificationSummary();
        reportUrgentItems(summary && summary.items);
      } catch {
        // still — beim nächsten Intervall erneut versuchen
      }
    },

    // Stabiler Schlüssel pro dringender Nachricht: primär die Konversation,
    // defensiv mit Fallbacks, falls Felder fehlen.
    itemKey(item) {
      if (item.idConversation) return `conversation:${item.idConversation}`;
      if (item.inboxId) return `inbox:${item.inboxId}`;
      return `case:${item.caseId}:${item.title}`;
    },

    evaluate(items) {
      const list = Array.isArray(items) ? items : [];

      if (list.length === 0) {
        // Keine dringenden Nachrichten mehr (gelesen o. Ä.) → Popup schließen
        // und quittierte IDs aufräumen.
        this.open = false;
        writeDismissedMap({});
        return;
      }

      const currentKeys = list.map((item) => this.itemKey(item));
      const dismissed = readDismissedMap();

      // Aufräumen: Einträge entfernen, deren Nachricht nicht mehr dringend/
      // ungelesen ist — so kann dieselbe Konversation später wieder alarmieren.
      const cleaned = Object.fromEntries(
        Object.entries(dismissed).filter(([key]) => currentKeys.includes(key)),
      );
      writeDismissedMap(cleaned);

      const hasNew = currentKeys.some((key) => !(key in cleaned));
      if (hasNew) {
        this.open = true;
      }
    },

    dismissAll() {
      const now = Date.now();
      const dismissed = readDismissedMap();
      const updated = { ...dismissed };
      this.urgentItems.forEach((item) => {
        updated[this.itemKey(item)] = now;
      });
      writeDismissedMap(updated);
      this.open = false;
    },

    openItem(item) {
      // Angeklickte Nachricht als quittiert merken: Das Öffnen markiert sie
      // i. d. R. als gelesen; bis dahin darf das Popup nicht erneut aufgehen.
      const dismissed = readDismissedMap();
      writeDismissedMap({ ...dismissed, [this.itemKey(item)]: Date.now() });
      this.open = false;

      // Deep-Link-Mechanismus identisch zur Glocke (NotificationBell.goToItem).
      if (item.inboxId) {
        this.$router.push({ name: "PageMessages", query: { inbox: String(item.inboxId) } });
        return;
      }
      const query = item.idConversation ? { conversation: String(item.idConversation) } : undefined;
      this.$router.push({ name: "PageCaseDetailNachrichten", params: { idCase: item.caseId }, query });
    },

    formatTime(raw) {
      if (!raw) return "";
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return raw;
        const mins = Math.floor((Date.now() - d.getTime()) / 60000);
        if (mins < 1) return "Gerade eben";
        if (mins < 60) return `vor ${mins} Min.`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `vor ${hours} Std.`;
        const days = Math.floor(hours / 24);
        if (days === 1) return "Gestern";
        if (days < 7) return `vor ${days} Tagen`;
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
      }
      return raw;
    },
  },
};
</script>

<style scoped>
/* ── Overlay ── */
.urgent-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050; /* über Glocken-Dropdown (9999) und App-Chrome */
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 16px 16px;
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(3px);
}

/* ── Card ── */
.urgent-card {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 72vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 24px 70px rgba(127, 29, 29, 0.35),
    0 0 0 1px rgba(220, 38, 38, 0.25);
}

.urgent-accent {
  flex-shrink: 0;
  height: 5px;
  background: linear-gradient(90deg, #dc2626, #ef4444, #f87171);
}

/* ── Header ── */
.urgent-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px 14px;
}

.urgent-icon-wrapper {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.urgent-icon {
  width: 22px;
  height: 22px;
  color: #dc2626;
  stroke-width: 2;
  animation: urgent-icon-pulse 1.6s ease-in-out infinite;
}

.urgent-icon-pulse {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: #ef4444;
  opacity: 0.35;
  animation: urgent-ping 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
  pointer-events: none;
}

@keyframes urgent-ping {
  75%,
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes urgent-icon-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.12);
  }
}

.urgent-header-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.urgent-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #7f1d1d;
}

.urgent-subtitle {
  font-size: 0.82rem;
  color: #9a3412;
  line-height: 1.3;
}

.urgent-count {
  flex-shrink: 0;
  min-width: 26px;
  height: 26px;
  padding: 0 8px;
  border-radius: 13px;
  background: #dc2626;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
}

/* ── Item list ── */
.urgent-list {
  flex: 1;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0 14px 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.urgent-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.urgent-item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.urgent-item-title {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.urgent-item-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.urgent-item-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.73rem;
  color: #b91c1c;
  font-weight: 500;
}

.urgent-item-dot {
  color: #fca5a5;
}

.urgent-open-btn {
  flex-shrink: 0;
  padding: 7px 16px;
  border: none;
  border-radius: 8px;
  background: #dc2626;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
}

.urgent-open-btn:hover {
  background: #b91c1c;
}

.urgent-open-btn:focus-visible {
  outline: 2px solid #7f1d1d;
  outline-offset: 2px;
}

/* ── Footer ── */
.urgent-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px 14px;
  border-top: 1px solid #fee2e2;
}

.urgent-later-btn {
  padding: 8px 18px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fff;
  color: #b91c1c;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.urgent-later-btn:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

.urgent-later-btn:focus-visible {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}

/* ── Transition (nur transform/opacity) ── */
.urgent-enter-active {
  transition: opacity 0.2s ease;
}
.urgent-leave-active {
  transition: opacity 0.15s ease;
}
.urgent-enter-active .urgent-card {
  transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease;
}
.urgent-enter-from,
.urgent-leave-to {
  opacity: 0;
}
.urgent-enter-from .urgent-card {
  transform: translateY(-14px) scale(0.97);
  opacity: 0;
}
</style>
